'use client';

import dynamic from 'next/dynamic';
import {useEffect, useState, type ReactNode} from 'react';

import {registerOpener, type RegisterInterestOpenOptions} from './register-interest-controller';
import type {RegisterInterestProject, RegisterInterestUnitType} from './types';

// The modal (form + zod schema + PhoneInput/Select/RadioGroup) ships to every
// page via the root layout, though most visitors never open it — code-split it
// and mount it only after the first open (kept mounted after that so the close
// transition still plays).
const RegisterInterestModal = dynamic(() => import('./RegisterInterestModal').then(m => m.RegisterInterestModal), {ssr: false});

export interface RegisterInterestProviderProps {
  projects: RegisterInterestProject[];
  unitTypes: RegisterInterestUnitType[];
  children: ReactNode;
}

export function RegisterInterestProvider({projects, unitTypes, children}: RegisterInterestProviderProps) {
  const [open, setOpen] = useState(false);
  // Stays true after the first open so the modal remains mounted (its close
  // transition plays) while the dynamic chunk only loads on that first open.
  const [mounted, setMounted] = useState(false);
  const [preselectedProject, setPreselectedProject] = useState<string | undefined>(undefined);
  const [preselectedPhase, setPreselectedPhase] = useState<string | undefined>(undefined);
  const [preselectedDesignType, setPreselectedDesignType] = useState<string | undefined>(undefined);

  useEffect(
    () =>
      registerOpener((options?: RegisterInterestOpenOptions) => {
        setPreselectedProject(options?.projectId);
        setPreselectedPhase(options?.phaseId);
        setPreselectedDesignType(options?.designTypeId);
        setMounted(true);
        setOpen(true);
      }),
    [],
  );

  return (
    <>
      {children}
      {mounted && (
        <RegisterInterestModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          projects={projects}
          unitTypes={unitTypes}
          preselectedProject={preselectedProject}
          preselectedPhase={preselectedPhase}
          preselectedDesignType={preselectedDesignType}
        />
      )}
    </>
  );
}

RegisterInterestProvider.displayName = 'RegisterInterestProvider';
